import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { user } = useAuth();
  const scope = user?.id ? JSON.stringify([user.id, user.role, user.cari_id || null]) : null;
  // Remount on identity changes: no render or delayed response can expose the old cart.
  return <ScopedCartProvider key={scope || "anonymous"} scope={scope}>{children}</ScopedCartProvider>;
}

export function cartStorageKey(scope) {
  return scope ? `vagenpar_cart_v2:${scope}` : null;
}

function ScopedCartProvider({ children, scope }) {
  const storageKey = cartStorageKey(scope);
  const [cart, setCart] = useState(() => {
    try {
      const saved = storageKey ? JSON.parse(localStorage.getItem(storageKey) || "[]") : [];
      return Array.isArray(saved) ? saved : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (!storageKey) return;
    try { localStorage.setItem(storageKey, JSON.stringify(cart)); }
    catch { /* Storage unavailable: keep the current cart in memory. */ }
  }, [cart, storageKey]);

  const addToCart = useCallback((item, qty = 1) => {
    const pack = Math.max(1, parseInt(item.paket_adedi, 10) || 1);
    const safeQty = Math.max(pack, Math.ceil((Number(qty) || pack) / pack) * pack);
    setCart((prev) => {
      const existing = prev.find((i) => i.stok_id === item.stok_id);
      if (existing) {
        return prev.map((i) =>
          i.stok_id === item.stok_id ? { ...i, miktar: i.miktar + safeQty } : i
        );
      }
      return [...prev, { ...item, miktar: safeQty }];
    });
  }, []);

  const removeFromCart = useCallback((stok_id) => {
    setCart((prev) => prev.filter((i) => i.stok_id !== stok_id));
  }, []);

  const updateQuantity = useCallback((stok_id, miktar) => {
    if (miktar <= 0) {
      setCart((prev) => prev.filter((i) => i.stok_id !== stok_id));
    } else {
      setCart((prev) => prev.map((i) => {
        if (i.stok_id !== stok_id) return i;
        const pack = Math.max(1, parseInt(i.paket_adedi, 10) || 1);
        const safeQty = Math.max(pack, Math.ceil((Number(miktar) || pack) / pack) * pack);
        return { ...i, miktar: safeQty };
      }));
    }
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const removeCartItems = useCallback((stokIds) => {
    const ids = new Set(stokIds || []);
    setCart((prev) => prev.filter((item) => !ids.has(item.stok_id)));
  }, []);

  const refreshCartItems = useCallback((items) => {
    const byId = new Map((items || []).map((item) => [item.stok_id, item]));
    setCart((prev) => prev.map((item) => byId.has(item.stok_id)
      ? { ...item, ...byId.get(item.stok_id) }
      : item));
  }, []);

  const cartCount = useMemo(() => cart.reduce((sum, i) => sum + i.miktar, 0), [cart]);
  const cartTotal = useMemo(() => cart.reduce((sum, i) => sum + i.b2b_fiyat * i.miktar, 0), [cart]);

  // Memoize context value to prevent re-renders when parent re-renders
  const contextValue = useMemo(
    () => ({ cart, addToCart, removeFromCart, removeCartItems, updateQuantity, clearCart, refreshCartItems, cartCount, cartTotal }),
    [cart, addToCart, removeFromCart, removeCartItems, updateQuantity, clearCart, refreshCartItems, cartCount, cartTotal]
  );

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
